import { translations } from '@/config/time';
import BigNumber from 'bignumber.js';
import numeral from 'numeral';

/**
 * @description: 文字格式中间省略号
 * @other substr,substring都可用于截取字符串
 * @param {string} e 需要格式化的字符串
 * @return {string}
 */
export const formatTextEllipsis = (e: string | undefined, startCount = 5, endCount = 3): string => {
  if (!e) return '';
  let str = '';
  if (e == '') return e;
  if (e && e.length > 10) {
    let start = e.substring(0, startCount);
    let end = e.substring(e.length - endCount, e.length);
    str = start + '...' + end;
  }
  return str;
};

/**
 * @description: 字符串格式化小数后几位
 * @param {string} str
 * @param {number} decimalPlaces
 * @return {*}
 */
export function formatDecimal(value: number | string | undefined | null = 0, decimals: number = 2): string {
  if (value === undefined || value === null) return '0';

  let num = 0;
  try {
    num = typeof value === 'string' ? parseFloat(value) : Number(value);

    if (isNaN(num)) return '0';
  } catch {
    return '0';
  }

  const isNegative = num < 0;
  const absoluteValue = Math.abs(num);

  const [integerPart, decimalPart = ''] = absoluteValue.toString().split('.');

  const truncatedDecimal = decimalPart.slice(0, decimals);

  const cleanedDecimal = truncatedDecimal.replace(/0+$/, '');

  const formattedNumber = cleanedDecimal.length > 0 ? `${integerPart}.${cleanedDecimal}` : integerPart;

  return isNegative ? `-${formattedNumber}` : formattedNumber;
}

/**
 * @description 将数值格式化成短式计数，转换成类似 101.4M 或 3.1K 的格式,加符号变成 -101.4M 或 -3.1K
 * @param amount string
 * @param symbol 是否加上符号
 * @returns
 */
export function formatCompactNumber(amount: number | string | undefined | null = 0, symbol = false): string {
  if (amount === undefined || amount === null) return '0';

  let num = 0;
  try {
    num = typeof amount === 'string' ? parseFloat(amount) : Number(amount);

    if (isNaN(num)) return '0';
  } catch {
    return '0';
  }

  const isNegative = num < 0;
  const absoluteAmount = Math.abs(num);

  let formattedAmount: string;

  if (absoluteAmount >= 1_000_000_000) {
    formattedAmount = `${(absoluteAmount / 1_000_000_000).toFixed(1)}B`;
  } else if (absoluteAmount >= 1_000_000) {
    formattedAmount = `${(absoluteAmount / 1_000_000).toFixed(1)}M`;
  } else if (absoluteAmount >= 1_000) {
    formattedAmount = `${(absoluteAmount / 1_000).toFixed(1)}K`;
  } else {
    formattedAmount = `${Number(absoluteAmount.toFixed(1))}`;
  }

  formattedAmount = formattedAmount.replace(/\.0([KMB])?$/, '$1');

  return symbol ? (isNegative ? `-$${formattedAmount}` : `+$${formattedAmount}`) : isNegative ? `-${formattedAmount}` : `+${formattedAmount}`;
}

// ===================================================================================================

// 时间单位定义
export type TimeUnit = 'seconds' | 'minutes' | 'hours' | 'days' | 'months' | 'years';

// 语言配置接口
export interface TimeTranslation {
  [key: string]: {
    [K in TimeUnit]: {
      short: string; // 简短形式 (如 's')
      long: string; // 完整形式 (如 'seconds ago')
      shortAgo: string; // 独立形式 (如 's ago')
    };
  };
}

// 时间单位配置
const TIME_UNITS: Array<{
  unit: TimeUnit;
  seconds: number;
  maxValue?: number;
}> = [
  { unit: 'seconds', seconds: 1, maxValue: 60 },
  { unit: 'minutes', seconds: 60, maxValue: 60 },
  { unit: 'hours', seconds: 3600, maxValue: 24 },
  { unit: 'days', seconds: 86400, maxValue: 30 },
  { unit: 'months', seconds: 2592000, maxValue: 12 },
  { unit: 'years', seconds: 31536000 },
];

interface FormatTimeOptions {
  lang?: string; // 语言
  style?: 'short' | 'long' | 'shortAgo'; // 显示风格
  fallbackLang?: string; // 后备语言
  now?: number; // 当前时间戳，用于测试
}

export const formatTimeAgo = (timestamp: number | undefined, options: FormatTimeOptions = {}): string => {
  if (timestamp == undefined) return '';
  const { lang = 'en', style = 'short', fallbackLang = 'en', now = Math.floor(Date.now() / 1000) } = options;

  // 获取语言配置，如果不存在则使用后备语言
  const trans = translations[lang] || translations[fallbackLang];

  // 计算时间差
  const diff = Math.max(0, now - timestamp);

  // 查找适合的时间单位
  const timeUnit =
    TIME_UNITS.find(({ seconds, maxValue }) => {
      const value = Math.floor(diff / seconds);
      return maxValue ? value < maxValue : true;
    }) || TIME_UNITS[TIME_UNITS.length - 1];

  // 计算最终值
  const value = Math.floor(diff / timeUnit.seconds);

  // 获取对应的翻译
  const translation = trans[timeUnit.unit][style];

  return `${value}${translation}`;
};

interface FormatPercentageOptions {
  showPositiveSign?: boolean;
  precision?: number;
  maxValue?: number;
  scale?: boolean; // 新增的参数，用于控制是否乘以100
}
/**
 * @description 生成价格变化百分比的文本
 * @param value string | number | undefined | null
 * @returns
 */

export const formatPercentage = (value: string | number | undefined | null, options: FormatPercentageOptions = {}) => {
  const {
    showPositiveSign = true,
    precision = 2,
    maxValue = 999,
    scale = false, // 默认值为 false，表示不乘以100
  } = options;

  const numericValue = new BigNumber(value || 0);

  const processedValue = scale ? numericValue.times(100) : numericValue;

  if (processedValue.eq(0)) return '0%';

  if (processedValue.isGreaterThan(scale ? maxValue * 100 : maxValue)) return `${showPositiveSign ? '+' : ''}${maxValue}k%`;

  const sign = processedValue.isNegative() ? '-' : showPositiveSign ? '+' : '';

  const formattedValue = processedValue.isInteger() ? processedValue.abs().toFixed(0) : processedValue.abs().toFixed(precision);

  return `${sign}${Number(formattedValue)}%`;
};

/**
 * @description 格式化盈亏  千位分隔符
 * @param value
 * @returns
 */
export const formatBalanceWithCommas = (value: string | number | undefined | null) => {
  try {
    if (value === null || value === undefined || value === '') return '$0';

    let numValue = new BigNumber(value);

    if (!numValue.isFinite()) return '$0';

    if (numValue.abs().isLessThan(0.01)) return '$0';

    return numeral(numValue).format('$0,0.00');
  } catch (error) {
    return '$0';
  }
};

/**
 * @description 将数值格式化成短式计数，转换成类似 101.4M 或 3.1K 的格式（用于数量或是市值）
 * @param amount string
 * @returns
 */
export function formatValueWithCompactNumber(amount: number | string | undefined | null = 0, symbol = false, decimal = 4): string {
  if (amount === undefined || amount === null) return '0';

  let num = 0;
  try {
    num = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
    if (isNaN(num)) return '0';
  } catch {
    return '0';
  }

  const isNegative = num < 0;
  const absoluteAmount = Math.abs(num);
  let formattedAmount: string;

  if (absoluteAmount >= 1_000_000_000) {
    formattedAmount = `${(absoluteAmount / 1_000_000_000).toFixed(1)}B`;
  } else if (absoluteAmount >= 1_000_000) {
    formattedAmount = `${(absoluteAmount / 1_000_000).toFixed(1)}M`;
  } else if (absoluteAmount >= 1_000) {
    formattedAmount = `${(absoluteAmount / 1_000).toFixed(2)}K`;
  } else {
    const str = absoluteAmount.toString();
    const decimalIndex = str.indexOf('.');
    if (decimalIndex === -1) return str;
    formattedAmount = str.substring(0, decimalIndex + decimal + 1);
  }

  formattedAmount = formattedAmount.replace(/\.0([KMB])?$/, '$1');

  return isNegative ? (symbol ? `-$${formattedAmount}` : `-$${formattedAmount}`) : symbol ? `$${formattedAmount}` : formattedAmount;
}

/**
 * @description 将数字转换为下标形式
 * @param num - 要转换的数字
 * @returns 下标形式的字符串
 */
function toSubscript(num: number): string {
  const subscriptMap: { [key: string]: string } = {
    '0': '₀',
    '1': '₁',
    '2': '₂',
    '3': '₃',
    '4': '₄',
    '5': '₅',
    '6': '₆',
    '7': '₇',
    '8': '₈',
    '9': '₉',
  };
  return num
    .toString()
    .split('')
    .map((char) => subscriptMap[char] || char)
    .join('');
}

/**
 * 格式化加密货币价格
 * @param price - 价格数值
 * @param options - 格式化选项
 * @returns 格式化后的价格字符串
 * @preview 0.0₄6305
 */
export function formatCryptoPrice(value: string | number): string {
  if (!value) return '0';

  const price = BigNumber(value);
  const zeroCount = countLeadingZerosAfterDecimal(value);
  if (zeroCount == 0) return formatDecimal(price.toString(), 4);
  if (zeroCount == 1) return formatDecimal(price.toString(), 6);
  if (zeroCount == 2) return formatDecimal(price.toString(), 7);
  if (zeroCount == 3) return formatDecimal(price.toString(), 8);

  if (zeroCount >= 4) {
    let p = BigNumber(price).toFixed(40);
    const zeroCount = p.split('.')[1]?.match(/^0+/)?.[0]?.length || 0;
    if (zeroCount > 3) {
      const significantDigits = p.toString().split('.')[1].slice(zeroCount);
      const truncatedDigits = significantDigits.slice(0);
      return `0.0${toSubscript(zeroCount)}${Number(truncatedDigits.slice(0, 4))}`;
    }
  }

  return formatDecimal(price.toString(), 4);
}

// 判断小数点后的零有多少个
export function countLeadingZerosAfterDecimal(value: number | string): number {
  // 将输入转换为字符串
  const str = typeof value === 'number' ? value.toString() : value;

  // 分割整数部分和小数部分
  const [, decimalPart = ''] = str.split('.');

  // 使用正则表达式匹配开头的连续零
  const leadingZerosMatch = decimalPart.match(/^0+/);

  // 返回匹配的零的个数，如果没有匹配则返回 0
  return leadingZerosMatch ? leadingZerosMatch[0].length : 0;
}

/**
 * @description 截断小数点0后3位
 * @param numStr
 * @returns
 */
export const truncateToThreeDecimals = (numStr: string) => {
  // 将字符串转换为浮动数值
  let num = parseFloat(numStr);

  // 如果数值为0或非数字，直接返回
  if (isNaN(num) || num === 0) return num.toFixed(3);

  // 使用 toFixed(3) 保证小数点后最多3位
  let truncated = num.toFixed(3);

  // 处理小数部分为0的情况，去掉末尾的0
  if (truncated.indexOf('.') !== -1) {
    truncated = truncated.replace(/\.?0+$/, '');
  }

  return truncated;
};

/**
 * 格式化小数，保留第一个非零位后两位
 * @param value 需要格式化的数值，支持number或string类型
 * @returns 格式化后的字符串
 * @example
 * formatFirstNonZeroDecimal(0.000001903988183161) // "0.0000019"
 * formatFirstNonZeroDecimal("0.000100001") // "0.0001"
 * formatFirstNonZeroDecimal(1.23456) // "1.23"
 * formatFirstNonZeroDecimal(null) // "0"
 */
export const formatFirstNonZeroDecimal = (value: string | number | null | undefined): string => {
  // 处理空值
  if (value === null || value === undefined || value === '') return '0';

  // 转换为数字
  const num = typeof value === 'string' ? Number(value) : value;

  // 处理非法数字
  if (isNaN(num)) return '0';
  if (!isFinite(num)) return num.toString();

  // 如果是0，直接返回
  if (num === 0) return '0';

  // 转换为字符串并去掉末尾的0
  let str = num.toFixed(20).replace(/\.?0+$/, '');

  // 如果是整数，直接返回
  if (!str.includes('.')) return str;

  // 找到第一个非零数字的位置
  const firstNonZero = str.match(/\.0*[1-9]/)?.[0];
  if (!firstNonZero) return str;

  const firstNonZeroIndex = str.indexOf(firstNonZero.slice(-1));
  const digitsToKeep = firstNonZeroIndex + 3; // 保留第一个非零数字后两位

  return str.slice(0, digitsToKeep);
};

/**
 * @description 格式化成  "2025/01/18 14:20:34"
 * @param date
 * @returns
 */
type TimeFormat = 'date' | 'minute' | 'second';
export const formatDate = (date: Date | string | number, format: TimeFormat = 'second'): string => {
  const d = new Date(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  if (format === 'date') {
    return `${year}/${month}/${day}`;
  }

  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');

  if (format === 'minute') {
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  }

  const seconds = String(d.getSeconds()).padStart(2, '0');
  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
};

// 02/11 16:05:52
export const formatTimestamp = (timestamp: number) => {
  const date = new Date(timestamp);

  // 补零函数
  const padZero = (num: number) => (num < 10 ? `0${num}` : num);

  const month = padZero(date.getMonth() + 1);
  const day = padZero(date.getDate());
  const hours = padZero(date.getHours());
  const minutes = padZero(date.getMinutes());
  const seconds = padZero(date.getSeconds());

  return `${month}/${day} ${hours}:${minutes}:${seconds}`;
};

interface LangConfig {
  [key: string]: {
    timeUnits: {
      day: string;
      hour: string;
      min: string;
      second: string;
    };
    smartWalletBought: string; // 模板字符串，用于格式化完整句子
  };
}

const langConfig: LangConfig = {
  en: {
    timeUnits: {
      day: 'd',
      hour: 'h',
      min: 'm',
      second: 's',
    },
    smartWalletBought: '{count} Smart wallets bought in {time}',
  },
  'zh-CN': {
    timeUnits: {
      day: '天',
      hour: '小时',
      min: '分钟',
      second: '秒',
    },
    smartWalletBought: '{count} 个聪明钱在 {time}内买入',
  },
  'zh-HK': {
    timeUnits: {
      day: '天',
      hour: '小時',
      min: '分鐘',
      second: '秒',
    },
    smartWalletBought: '{count} 個聰明錢在 {time}內買入',
  },
};

// 格式化完整句子函数
export const formatSmartWalletBoughtMessage = (count: number, totalSeconds: number, lang: string): string => {
  // 默认使用英语作为后备语言
  const config = langConfig[lang] || langConfig.en;

  // 格式化时间部分
  let timeValue: number;
  let timeUnit: string;

  if (totalSeconds >= 86400) {
    timeUnit = config.timeUnits.day;
    timeValue = Math.ceil(totalSeconds / 86400);
  } else if (totalSeconds >= 3600) {
    timeUnit = config.timeUnits.hour;
    timeValue = Math.ceil(totalSeconds / 3600);
  } else if (totalSeconds >= 60) {
    timeUnit = config.timeUnits.min;
    timeValue = Math.ceil(totalSeconds / 60);
  } else {
    timeUnit = config.timeUnits.second;
    timeValue = totalSeconds;
    if (timeValue === 0) timeValue = 1;
  }

  // 格式化时间字符串 - 英文没有空格，中文保留空格
  const timeString = lang.startsWith('zh-') ? `${timeValue} ${timeUnit}` : `${timeValue}${timeUnit}`;

  // 处理1秒的特殊情况
  let message = config.smartWalletBought.replace('{count}', String(count)).replace('{time}', timeString);

  if (totalSeconds === 1 && lang.startsWith('zh-')) {
    message = message.replace('内', '');
  }

  return message;
};
